// Added by Sejune Oh on 2019-03-08
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import StoreBase, { StoreBaseConstructorProps } from './StoreBase'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { GetQueryReturn, API_VERSION } from '../../helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface DataStorageBaseConstructor extends StoreBaseConstructorProps {
    apiSubUrl: string
}

interface DuplicatedCheckParams {
    value: string
    field: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class DataStorageBase<T> extends StoreBase<T> {
    _apiSubUrl: string
    _proxyToken: string = ''

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    constructor({ storeName, apiSubUrl, _root }: DataStorageBaseConstructor) {
        super({ storeName, _root })
        this._apiSubUrl = apiSubUrl
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    get _apiUrl() {
        if (!this._proxyToken) throw new Error('Set proxy token first before use api.')

        return `${API_VERSION}/data/${this._proxyToken}/${this._apiSubUrl}`
    }

    _getAPIUrl(foreignKeyId?: string, foreignSubUrl?: string) {
        if (!foreignKeyId) return this._apiUrl

        return `${this._apiUrl}/${foreignKeyId}/${foreignSubUrl}`
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    get(id: string, foreignKeyId?: string, foreignSubUrl?: string): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const apiUrl = this._getAPIUrl(foreignKeyId, foreignSubUrl)
                const { data: rtnData } = await super.get({ url: `${apiUrl}/${id}` })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    getSingleWithoutId(): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnData } = await super.get({ url: `${this._apiUrl}` })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    getAll(getQuery?: string, foreignKeyId?: string, foreignSubUrl?: string): Promise<GetQueryReturn<T>> {
        return new Promise(async (resolve, reject) => {
            try {
                const apiUrl = this._getAPIUrl(foreignKeyId, foreignSubUrl)
                const { data: rtnData } = await super.get({
                    url: `${apiUrl}${getQuery ? `?${getQuery}` : ''}`,
                })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    add(element: T, foreignKeyId?: string, foreignSubUrl?: string): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const apiUrl = this._getAPIUrl(foreignKeyId, foreignSubUrl)
                const { data: rtnData } = await super.post({
                    url: `${apiUrl}`,
                    data: element,
                })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    postCustom(
        { subUrl, element }: { element?: T; subUrl?: string },
        foreignKeyId?: string,
        foreignSubUrl?: string
    ): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const apiUrl = this._getAPIUrl(foreignKeyId, foreignSubUrl)
                const { data: rtnData } = await super.post({
                    url: `${apiUrl}${subUrl}`,
                    data: element,
                })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    remove(id: string, foreignKeyId?: string, foreignSubUrl?: string): Promise<{}> {
        return new Promise(async (resolve, reject) => {
            try {
                const apiUrl = this._getAPIUrl(foreignKeyId, foreignSubUrl)
                await super.delete({ url: `${apiUrl}/${id}` })
                resolve(true)
            } catch (err) {
                reject(err)
            }
        })
    }

    update(id: string, element: T, foreignKeyId?: string, foreignSubUrl?: string): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const apiUrl = this._getAPIUrl(foreignKeyId, foreignSubUrl)
                const { data: rtnData } = await super.put({
                    url: `${apiUrl}/${id}`,
                    data: {
                        ...element,
                    },
                })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    updateWithoutId(element: T): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnData } = await super.put({
                    url: `${this._apiUrl}`,
                    data: {
                        ...element,
                    },
                })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    async checkDuplicatedByField(
        { value, field }: DuplicatedCheckParams,
        foreignKeyId?: string,
        foreignSubUrl?: string
    ): Promise<boolean> {
        const filter = { $op: 'and', [`${field}`]: `${value}` }
        const path = this.getQueryPath({
            pageIndex: 0,
            orderBy: `${field}`,
            filter: JSON.stringify(filter),
        })
        const rtnData = foreignKeyId
            ? await this.getAll(path, foreignKeyId, foreignSubUrl as string)
            : await this.getAll(path)
        return (rtnData.items as Array<T>).length > 0
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    setProxyToken = (proxyToken: string) => {
        this._proxyToken = proxyToken
    }
}
